import { TitleBar } from "@shopify/app-bridge-react";
import {
  Banner,
  Card,
  FormLayout,
  Icon,
  Layout,
  Page,
  PageActions,
  Select,
  TextField,
  Text,
  Stack,
} from "@shopify/polaris";
import {
  EmailMajor,
  ImageMajor,
  LinkMinor,
  PhoneMajor,
} from "@shopify/polaris-icons";
import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../components/global/appContext";
import useCompare from "../../../../hooks/useCompare";
import useLanguageSelector from "../../../../hooks/useLanguageSelector";
import LanguageSelector from "../../../../components/components/LanguageSelector";
import SEOEditor from "../../components/SeoEditor";
import CustomFields from "./form/CustomFields";
import LocationContainerAutocomplete from "./form/LocationContainerAutocomplete";
import MapboxMap from "./form/MapboxMap";
import OpeningHours from "./form/OpeningHours";
import LocationFormSkeleton from "./form/Skeleton";
import FormTags from "./form/Tags";
import PriorityField from "../../components/PriorityField";

import { useGeocode } from "../../../../hooks/queries/geocode";
import { useGetLocation } from "../../../../hooks/queries/locations";

export default function LocationForm(props) {
  const { location, setLocation, errors = {} } = props;
  const { t } = useTranslation();

  const { setMessage, languageTranslateIconUrl } = useContext(AppContext);
  let { id = "new" } = useParams();
  const { data, isLoading } = useGetLocation(id);
  
  // Use the new language selector hook
  const {
    currentLocale,
    isLocalizationEnabled,
    localeOptions,
    getLocaleDisplayName
  } = useLanguageSelector(location);
  
  const [selectedNameLocale, setSelectedNameLocale] = useState("en");
  const saveBarRef = useRef(null);

  useEffect(() => setLocation(data), [data]);

  useEffect(() => {
    if (currentLocale && selectedNameLocale === "en") {
      setSelectedNameLocale(currentLocale);
    }
  }, [currentLocale]);



  // Compare current form state with server data to detect unsaved changes
  const locationsAreEqual = useCompare(data || {}, location || {});
  const hasUnsavedChanges = data && location && !locationsAreEqual;
  const hasErrors = errors && Object.keys(errors).length > 0;

  // Control the save bar visibility based on unsaved changes or errors
  useEffect(() => {
    if (saveBarRef.current) {
      if (hasUnsavedChanges || hasErrors) {
        saveBarRef.current.show();
      } else {
        saveBarRef.current.hide();
      }
    }
  }, [hasUnsavedChanges, hasErrors]);

  const geocodeMutation = useGeocode({
    onSuccess: (data) => {
      const [lat, lng] = data;
      setLocationCoords({ lat, lng });
    },
    onError: () => setMessage(t("location.validation.addressNotFound"), true),
  });

  if (isLoading || !location) return <LocationFormSkeleton />;

    const handleUpdate = (key, value) =>
    setLocation((oldLocation) => ({ ...oldLocation, [key]: value }));

  // Handle name field updates with translation support
  const handleNameUpdate = (value) => {
    const currentLocale = location?.current_locale || "en";
    
    if (selectedNameLocale === currentLocale) {
      // Default locale - update main name field
      handleUpdate("name", value);
    } else {
      // Non-default locale - update translations (what backend expects)
      const newTranslations = location.translations ? { ...location.translations } : {};
      if (!newTranslations.name) {
        newTranslations.name = {};
      }
      newTranslations.name[selectedNameLocale] = value;
      handleUpdate("translations", newTranslations);
    }
  };

  // Get name value for current locale
  const getNameValue = () => {
    const currentLocale = location?.current_locale || "en";
    
    if (selectedNameLocale === currentLocale) {
      return location.name || "";
    } else {
      // Check both sources: location_translations (from server) and translations (being edited)
      const savedTranslation = location.location_translations?.name?.[selectedNameLocale];
      const editingTranslation = location.translations?.name?.[selectedNameLocale];
      
      // Prefer the editing translation if it exists, otherwise use saved translation
      return editingTranslation !== undefined ? editingTranslation : (savedTranslation || "");
    }
  };

  // Get placeholder text for name field
  const getNamePlaceholder = () => {
    const currentLocale = location?.current_locale || "en";
    
    if (selectedNameLocale === currentLocale) {
      return t("location.form.namePlaceholder");
    } else {
      const defaultName = location.name || "";
      return defaultName ? t("location.form.nameHelpDefault", { defaultName }) : t("location.form.namePlaceholder");
    }
  };

  const setLocationCoords = (coords) =>
      setLocation((oldLocation) => ({
        ...oldLocation,
        lat: coords.lat,
        lng: coords.lng,
      }));

  // Handle save action (triggered by Save Bar)
  const handleSave = () => {
    // Hide Save Bar immediately on new location saves to prevent shake animation
    // If errors occur, the useEffect will show it again
    if (saveBarRef.current && id === "new") {
      saveBarRef.current.hide();
    }
    if (props.saveAction?.onAction) {
      props.saveAction.onAction();
    }
  };

  // Handle discard action (triggered by Discard button in Save Bar)
  const handleDiscard = () => {
    if (data) {
      setLocation(data);
    }
  };

  const textFieldProps = (key) => ({
    value: location[key] || "",
    onChange: (value) => handleUpdate(key, value),
    error: errors[key]?.join(", "),
  });

  const autoCompleteProps = (key) => ({
    value: location[key] || "",
    updateValue: (value) => handleUpdate(key, value),
    error: errors[key]?.join(", "),
  });

  const isDefaultNameLocale = selectedNameLocale === currentLocale;

  // Language selector component for name field
  const nameLanguageSelector = (
    <LanguageSelector
      options={localeOptions}
      value={selectedNameLocale}
      onChange={setSelectedNameLocale}
    />
  );

  return (
      <>
        <ui-save-bar ref={saveBarRef} id="location-save-bar">
          <button 
            variant="primary" 
            onClick={handleSave}
            disabled={props.saveAction?.loading}
            loading={props.saveAction?.loading ? "" : undefined}
          />
          <button onClick={handleDiscard} />
        </ui-save-bar>
        <Page>
          <TitleBar
              title={props.title}
              primaryAction={props.pagePrimaryAction}
              secondaryActions={props.pageSecondaryActions}
              breadcrumbs={[{ content: t("places.navigation.locations"), url: "/places/locations" }]}
          />
      <Layout>
        {errors.base && (
          <Layout.Section>
            <Banner status="critical">
              <p>{errors.base.join(" ")}</p>
            </Banner>
          </Layout.Section>
        )}
            <Layout.Section>
                            <Card>
                <Card.Section
                  title={
                    <Stack distribution="equalSpacing" alignment="center">
                      <Text variant="headingMd" as="h2">{t("location.form.basicInformation")}</Text>
                      {nameLanguageSelector}
                    </Stack>
                  }
                >
                  {isLocalizationEnabled && !isDefaultNameLocale && (
                    <div style={{ marginBottom: '1rem' }}>
                      <Text variant="bodyMd" color="subdued">
                        {t("seo.editingLocaleHelp", {
                          locale: getLocaleDisplayName(selectedNameLocale, localeOptions),
                          defaultLocale: getLocaleDisplayName(currentLocale, localeOptions)
                        })}
                      </Text>
                    </div>
                  )}
                  <TextField
                    label={t("location.form.name")}
                    placeholder={getNamePlaceholder()}
                    value={getNameValue()}
                    onChange={handleNameUpdate}
                    error={errors.name?.join(", ")}
                  />
                </Card.Section>
                <Card.Section
                    title={t("location.form.address")}
                    actions={[
                      {
                        content: t("location.form.findOnMapButton"),
                        onClick: () => geocodeMutation.mutate(location),
                        loading: geocodeMutation.isLoading,
                      },
                    ]}
                >
                                    <FormLayout>
                    <TextField
                        label={t("location.form.addressLine1")}
                        placeholder={t("location.form.addressPlaceholder")}
                        {...textFieldProps("address_line_1")}
                    />
                    <TextField
                        label={t("location.form.addressLine2")}
                        {...textFieldProps("address_line_2")}
                    />
                    <FormLayout.Group>
                      <TextField
                          label={t("location.form.postalZipCode")}
                          {...textFieldProps("postal_zip_code")}
                      />
                      <LocationContainerAutocomplete
                        label={t("location.form.city")}
                        {...autoCompleteProps("city_name")}
                        options={location?.available_location_containers?.cities || []}
                      />
                      <LocationContainerAutocomplete
                        label={t("location.form.stateProvinceRegion")}
                        {...autoCompleteProps("state_province")}
                        options={location?.available_location_containers?.regions || []}
                      />
                      <LocationContainerAutocomplete
                        label={t("location.form.country")}
                        {...autoCompleteProps("country_name")}
                        options={location?.available_location_containers?.countries || []}
                      />
                    </FormLayout.Group>
                  </FormLayout>
                </Card.Section>
                <Card.Section title={t("location.form.details")}>
                  <FormLayout>
                    <TextField
                        label={t("location.form.phone")}
                        type="tel"
                        autoComplete="no"
                        placeholder={t("location.form.phonePlaceholder")}
                        prefix={<Icon source={PhoneMajor} color="subdued" />}
                        {...textFieldProps("phone_number")}
                    />
                    <TextField
                        label={t("location.form.email")}
                        type="email"
                        autoComplete="no"
                        placeholder={t("location.form.emailPlaceholder")}
                        prefix={<Icon source={EmailMajor} color="subdued" />}
                        {...textFieldProps("email")}
                    />
                    <TextField
                        label={t("location.form.image")}
                        type="url"
                        autoComplete="no"
                        placeholder={t("location.form.imagePlaceholder")}
                        {...textFieldProps("image_url")}
                        prefix={<Icon source={ImageMajor} color="subdued" />}
                    />
                    <TextField
                        label={t("location.form.website")}
                        type="url"
                        autoComplete="no"
                        placeholder={t("location.form.websitePlaceholder")}
                        {...textFieldProps("website")}
                        prefix={<Icon source={LinkMinor} color="subdued" />}
                    />
                  </FormLayout>
                </Card.Section>

                <OpeningHours
                    location={location}
                    setLocation={setLocation}
                    errors={errors}
                />
              </Card>

                            <CustomFields location={location} setLocation={setLocation} />

              <SEOEditor
                area={location}
                newLocation={id === "new"}
                handleUpdate={handleUpdate}
              />
            </Layout.Section>

            <Layout.Section secondary>
              <Card>
                {(errors?.lat || errors?.lng) && (
                    <Card.Section>
                      <Banner
                          status="critical"
                          action={{
                            content: t("location.form.findOnMapButton"),
                            onAction: () => geocodeMutation.mutate(location),
                            loading: geocodeMutation.isLoading,
                          }}
                      >
                        <p>{t("location.validation.findOnMap")}</p>
                      </Banner>
                    </Card.Section>
                )}
                <Card.Section flush>
                  <MapboxMap
                      locationCoords={
                          location.lat &&
                          location.lng && {
                            lat: location.lat,
                            lng: location.lng,
                          }
                      }
                      setLocationCoords={setLocationCoords}
                  />
                </Card.Section>
              </Card>
              <FormTags location={location} setLocation={setLocation} />
              <Card title={t("location.form.status")} sectioned>
                <FormLayout>
                  <Select
                      label={t("location.form.status")}
                      labelHidden
                      options={[
                        { label: t("location.form.statusDraft"), value: "draft" },
                        { label: t("location.form.statusActive"), value: "published" },
                      ]}
                      onChange={(value) => handleUpdate("status", value)}
                      value={location.status}
                  />
                </FormLayout>
              </Card>
              <Card title={t("location.form.priority")} sectioned>
                <FormLayout>
                  <PriorityField {...textFieldProps("priority")} labelHidden helpText={t("location.form.priorityHelpText")} />
                </FormLayout>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <PageActions
                  primaryAction={props.footerPrimaryAction}
                  secondaryActions={props.footerSecondaryActions}
              />
            </Layout.Section>
          </Layout>
        </Page>
      </>
  );
}